import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import ViewToggle from "./shared/ViewToggle";
import SearchBar from "./shared/SearchBar";
import BulkActionBar from "./shared/BulkActionBar";
import "./shared/shared.css";
import "./HistoryPage.css";

export default function HistoryPage({ onLoadConversation }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("forge-history-view") || "card");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const fetchConversations = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/chat/conversations`);
      const data = await res.json();
      setConversations(
        (data.conversations || [])
          .filter((c) => c.integrationName && !c.integrationName.match(/^[0-9a-f]{8}-/))
          .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))
      );
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const filtered = conversations.filter((c) =>
    !search || c.integrationName.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const deleteConversation = async (name) => {
    try {
      await authFetch(`${API_BASE}/chat/conversations/${name}`, { method: "DELETE" });
      setConversations((prev) => prev.filter((c) => c.integrationName !== name));
      setSelected((prev) => { const next = new Set(prev); next.delete(name); return next; });
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} conversations? This cannot be undone.`)) return;
    for (const name of selected) {
      await deleteConversation(name);
    }
    setSelected(new Set());
  };

  const loadConversation = async (name) => {
    try {
      const res = await authFetch(`${API_BASE}/chat/conversations/${name}`);
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "Request failed");
      if (onLoadConversation) onLoadConversation(data);
    } catch (err) {
      console.error("Failed to load conversation:", err);
    }
  };

  return (
    <div className="history-page">
      <div className="page-header">
        <h2>Conversation History</h2>
        <div className="page-header-right">
          <span className="apps-stat">{conversations.length} conversations</span>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <SearchBar value={search} onChange={setSearch} placeholder="Search conversations..." />
        </div>
        <div className="page-toolbar-right">
          <button className="history-refresh-btn" onClick={fetchConversations} title="Refresh">&#x21bb;</button>
          <ViewToggle view={view} onChange={setView} storageKey="forge-history-view" />
        </div>
      </div>

      <BulkActionBar
        count={selected.size}
        total={filtered.length}
        onSelectAll={() => setSelected(new Set(filtered.map((c) => c.integrationName)))}
        onDeselectAll={() => setSelected(new Set())}
      >
        <button className="bulk-btn-danger" onClick={bulkDelete}>Delete Selected</button>
      </BulkActionBar>

      <div className="page-body">
        {loading ? (
          <div className="page-empty">Loading conversations...</div>
        ) : filtered.length === 0 ? (
          <div className="page-empty">
            {search ? `No conversations matching "${search}"` : "No conversation history yet."}
          </div>
        ) : view === "table" ? (
          <table className="data-table">
            <thead>
              <tr>
                <th className="td-checkbox">
                  <input
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((c) => c.integrationName)) : new Set())}
                  />
                </th>
                <th>Name</th>
                <th>Messages</th>
                <th>Last Edited</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((conv) => (
                <tr key={conv.integrationName}>
                  <td className="td-checkbox">
                    <input type="checkbox" checked={selected.has(conv.integrationName)} onChange={() => toggleSelect(conv.integrationName)} />
                  </td>
                  <td data-label="Name">{conv.integrationName}</td>
                  <td data-label="Messages">{conv.messageCount}</td>
                  <td data-label="Last Edited" style={{ color: "var(--color-text-faint)", fontSize: 12 }}>
                    {conv.updatedAt ? new Date(conv.updatedAt).toLocaleDateString() : "—"}
                  </td>
                  <td data-label="Actions">
                    <div className="td-actions">
                      <button className="apps-btn apps-btn-edit" onClick={() => loadConversation(conv.integrationName)}>Resume</button>
                      <button className="apps-btn apps-btn-danger" onClick={() => { if (window.confirm(`Delete "${conv.integrationName}" history?`)) deleteConversation(conv.integrationName); }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="history-grid">
            {filtered.map((conv) => (
              <div key={conv.integrationName} className="history-card">
                <input type="checkbox" checked={selected.has(conv.integrationName)} onChange={() => toggleSelect(conv.integrationName)} style={{ flexShrink: 0 }} />
                <div className="history-card-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4H16V14H6L4 16V4Z" />
                  </svg>
                </div>
                <div className="history-card-info" onClick={() => loadConversation(conv.integrationName)} style={{ cursor: "pointer", flex: 1 }}>
                  <span className="history-card-name">{conv.integrationName}</span>
                  <span className="history-card-meta">
                    {conv.messageCount} messages
                    {conv.updatedAt && ` · ${new Date(conv.updatedAt).toLocaleDateString()}`}
                  </span>
                </div>
                <button
                  className="history-delete-btn"
                  onClick={() => { if (window.confirm(`Delete "${conv.integrationName}" history?`)) deleteConversation(conv.integrationName); }}
                  title="Delete"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
